// ============================================
// AlpenScreen - Waiting Room (Premium Alpine Design)
// File: frontend/src/pages/WaitingRoom.jsx
// Purpose: Show device registration success and wait for content assignment
// ============================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader, Monitor, Wifi, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api';

const WaitingRoom = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const screenId = searchParams.get('screenId');
  const screenName = searchParams.get('screenName');
  
  const [status, setStatus] = useState('registered');
  const [dots, setDots] = useState('');
  const [ws, setWs] = useState(null);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!screenId) {
      navigate('/connect');
      return;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws`;
    
    console.log('🔌 Connecting to WebSocket:', wsUrl);
    
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('✅ WebSocket connected');
      setStatus('waiting');
      
      websocket.send(JSON.stringify({
        type: 'register',
        clientType: 'player',
        screenId: screenId
      }));
    };

    websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('📨 WebSocket message:', message);

        if (message.type === 'playlist_assigned' && message.screenId === screenId) {
          console.log('🎉 Playlist assigned! Starting player...');
          setStatus('ready');
          
          setTimeout(() => {
            navigate(`/player/${screenId}`);
          }, 2000);
        }

        if (message.type === 'screen_update' && message.data?.id === screenId) {
          if (message.data.playlist_id) {
            console.log('🎉 Playlist detected! Starting player...');
            setStatus('ready');
            
            setTimeout(() => {
              navigate(`/player/${screenId}`);
            }, 2000);
          }
        }
      } catch (err) {
        console.error('WebSocket message error:', err);
      }
    };

    websocket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('🔌 WebSocket disconnected');
    };

    setWs(websocket);

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [screenId, navigate]);

  // Poll for playlist assignment (fallback)
  useEffect(() => {
    if (!screenId || status === 'ready') return;

    const checkPlaylist = async () => {
      try {
        const response = await fetch(`${API_URL}/screens/uuid/${screenId}`);
        if (response.ok) {
          const data = await response.json();
          console.log('📊 Polling response:', data);
          if (data.playlist_id) {
            console.log('🎉 Playlist detected via polling! Starting player...');
            setStatus('ready');
            setTimeout(() => {
              navigate(`/player/${screenId}`);
            }, 2000);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Check immediately on mount
    checkPlaylist();
    
    const interval = setInterval(checkPlaylist, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [screenId, status, navigate]);

  if (!screenId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E7FCC] via-[#1B6DB8] to-[#185CA4] flex items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden">
        <div className="pt-6 pb-6 px-6 md:pt-8 md:pb-12 md:px-12">
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 animate-pulse shadow-lg">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {t('waitingRoom.registered', 'Gerät erfolgreich registriert!')}
            </h1>
            <p className="text-xl text-gray-600 font-medium">
              {screenName || t('waitingRoom.yourDevice', 'Ihr Gerät')}
            </p>
          </div>

          {/* Status Cards */}
          <div className="space-y-4 mb-8">
            
            {/* Step 1: Connected */}
            <div className="flex items-center gap-4 p-5 bg-green-50 border-2 border-green-200 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center shadow-md">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg text-green-900">
                  {t('waitingRoom.step1', 'Verbindung hergestellt')}
                </p>
                <p className="text-base text-green-700">
                  {t('waitingRoom.step1Desc', 'Gerät ist mit AlpenScreen verbunden')}
                </p>
              </div>
            </div>

            {/* Step 2: Waiting/Ready */}
            <div className={`flex items-center gap-4 p-5 border-2 rounded-xl transition-all ${
              status === 'ready' 
                ? 'bg-green-50 border-green-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                status === 'ready' ? 'bg-green-500' : 'bg-[#1E7FCC]'
              }`}>
                {status === 'ready' ? (
                  <CheckCircle className="w-7 h-7 text-white" />
                ) : (
                  <Loader className="w-7 h-7 text-white animate-spin" />
                )}
              </div>
              <div className="flex-1">
                <p className={`font-bold text-lg ${
                  status === 'ready' ? 'text-green-900' : 'text-blue-900'
                }`}>
                  {status === 'ready' 
                    ? t('waitingRoom.step2Complete', 'Inhalte zugewiesen!')
                    : t('waitingRoom.step2', 'Warte auf Inhalte') + dots
                  }
                </p>
                <p className={`text-base ${
                  status === 'ready' ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {status === 'ready'
                    ? t('waitingRoom.step2CompleteDesc', 'Starte Wiedergabe...')
                    : t('waitingRoom.step2Desc', 'Administrator wird nun Inhalte zuweisen')
                  }
                </p>
              </div>
            </div>

            {/* Step 3: Starting Player */}
            {status === 'ready' && (
              <div className="flex items-center gap-4 p-5 bg-green-50 border-2 border-green-200 rounded-xl animate-pulse">
                <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center shadow-md">
                  <Monitor className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg text-green-900">
                    {t('waitingRoom.starting', 'Player wird gestartet...')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Device Info Grid */}
          <div className="grid grid-cols-3 gap-4 p-5 bg-gray-50 rounded-2xl border-2 border-gray-100">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-3">
                <Wifi className="w-6 h-6 text-[#1E7FCC]" />
              </div>
              <p className="text-sm text-gray-600 mb-1 font-medium">
                {t('waitingRoom.connection', 'Verbindung')}
              </p>
              <p className="text-base font-bold text-gray-900">
                {ws?.readyState === WebSocket.OPEN 
                  ? t('waitingRoom.online', 'Online') 
                  : t('waitingRoom.connecting', 'Verbinde...')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-3">
                <Monitor className="w-6 h-6 text-[#1E7FCC]" />
              </div>
              <p className="text-sm text-gray-600 mb-1 font-medium">
                {t('waitingRoom.status', 'Status')}
              </p>
              <p className="text-base font-bold text-gray-900">
                {status === 'ready' 
                  ? t('waitingRoom.ready', 'Bereit') 
                  : t('waitingRoom.waiting', 'Wartet')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-3">
                <Clock className="w-6 h-6 text-[#1E7FCC]" />
              </div>
              <p className="text-sm text-gray-600 mb-1 font-medium">
                {t('waitingRoom.time', 'Zeit')}
              </p>
              <p className="text-base font-bold text-gray-900">
                {new Date().toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center pt-6 border-t-2 border-gray-100">
            <p className="text-base text-gray-600 mb-4">
              <svg className="w-5 h-5 inline-block mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('waitingRoom.helpText', 'Ihr Administrator wurde benachrichtigt. Inhalte werden in Kürze zugewiesen.')}
            </p>
            <button
              onClick={() => navigate('/connect')}
              className="text-base text-[#1E7FCC] hover:text-[#1B6DB8] font-semibold transition-colors inline-flex items-center gap-2 group"
            >
              <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('waitingRoom.backToConnect', 'Zurück zur Verbindungsseite')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitingRoom;